import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/common/Combobox";
import { useResource } from "@/hooks/useResource";
import api, { apiErrorMessage } from "@/lib/api";
import { fmtNumber } from "@/lib/format";
import { toast } from "sonner";
import {
  Plus, Trash2, Loader2, ShieldAlert, AlertTriangle, CheckCircle2, XCircle, Package,
} from "lucide-react";
import {
  PRODUCT_TYPES, productTypeLabel, hasApprovedRecipe, explodeRecipe, aggregateRequirements, fetchRecipe,
} from "@/lib/production";

const today = () => new Date().toISOString().slice(0, 10);

export function PlanBuilderDialog({ open, onOpenChange, onCreated, prefill, prefillNote, prefillSourceOrders }) {
  const { data: prodData } = useResource("/products", { enabled: open });
  const { data: invData } = useResource("/inventory", { enabled: open });
  const products = (Array.isArray(prodData) ? prodData : []).filter((p) => p.active);
  const inventory = useMemo(() => (Array.isArray(invData) ? invData : []), [invData]);
  const availMap = useMemo(() => {
    const m = {};
    inventory.forEach((i) => { m[i.ingredientId] = { available: Number(i.available) || 0, unit: i.unit, name: i.name }; });
    return m;
  }, [inventory]);

  const [section, setSection] = useState("BAKERY");
  const [date, setDate] = useState(today());
  const [lines, setLines] = useState([{ productId: "", plannedQty: "", recipe: null, loading: false }]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (prefill?.length) {
      // Order Consolidation handoff: prefill product + net qty. Recipe loads via the normal pickProduct
      // path (approved recipe required) and stock/requirements revalidate on open as usual.
      setSection("BAKERY"); setDate(today());
      setLines(prefill.map((pf) => ({ productId: "", plannedQty: pf.plannedQty ?? "", recipe: null, loading: false, _code: pf.productCode })));
      return;
    }
    setSection("BAKERY"); setDate(today()); setLines([{ productId: "", plannedQty: "", recipe: null, loading: false }]);
  }, [open]);

  // resolve prefilled product codes once the product list is available
  useEffect(() => {
    if (!open || !products.length) return;
    lines.forEach((l, i) => {
      if (l._code && !l.productId) {
        const p = products.find((x) => x.productId === l._code) || products.find((x) => x.id === l._code);
        if (p) {
          pickProduct(i, p.id);
          setLine(i, { plannedQty: l.plannedQty });
        }
      }
    });
  }, [open, products]);

  const sectionProducts = products.filter(
    (p) => String(p.productType || "BAKERY").toUpperCase().replace(/[\s-]/g, "_") === section
  );

  const setLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, { productId: "", plannedQty: "", recipe: null, loading: false }]);
  const removeLine = (i) => setLines((ls) => (ls.length === 1 ? ls : ls.filter((_, idx) => idx !== i)));

  const pickProduct = async (i, productId) => {
    const p = products.find((x) => x.id === productId);
    setLine(i, { productId, recipe: null, loading: hasApprovedRecipe(p) });
    if (hasApprovedRecipe(p)) {
      try {
        const rec = await fetchRecipe(p.currentRecipeId);
        if (!rec) throw new Error("Approved recipe not found");
        setLine(i, { recipe: rec, loading: false });
      } catch (e) {
        setLine(i, { recipe: null, loading: false });
        toast.error(apiErrorMessage(e, "Could not load approved recipe"));
      }
    }
  };

  // Build derived lines (with requirements) for display + payload.
  const built = lines.map((l) => {
    const p = products.find((x) => x.id === l.productId);
    const requirements = explodeRecipe(l.recipe, l.plannedQty);
    return { l, p, requirements };
  });
  const aggregated = aggregateRequirements(built.map((b) => ({ requirements: b.requirements })));

  const readyLines = built.filter((b) => b.p && hasApprovedRecipe(b.p) && Number(b.l.plannedQty) > 0);
  const blockedLines = built.filter((b) => b.p && !hasApprovedRecipe(b.p));
  const canSave = readyLines.length > 0 && blockedLines.length === 0;

  const doCreate = async () => {
    setConfirmOpen(false); setSaving(true);
    const payload = {
      date, section: productTypeLabel(section), status: "Planned",
      ...(prefill?.length ? {
        source: "ORDER_CONSOLIDATION",
        remarks: `${prefillNote || "From Order Consolidation"}${prefillSourceOrders?.length ? ` | source orders: ${prefillSourceOrders.join(", ")}` : ""}`,
      } : {}),
      lines: readyLines.map((b) => ({
        productId: b.p.productId, sku: b.p.sku || "", productName: b.p.name,
        productType: b.p.productType || section, category: b.p.category || "",
        recipeId: b.p.currentRecipeId, recipeVersion: b.p.currentRecipeVersion,
        plannedQty: Number(b.l.plannedQty) || 0, unit: b.p.productUnit || b.p.yieldUnit || "pcs",
        yieldQty: Number(b.l.recipe?.yieldQty) || 0, yieldUnit: b.l.recipe?.yieldUnit || "pcs",
        requirements: b.requirements,
      })),
    };
    try {
      const res = await api.post("/production-plans", payload);
      toast.success("Production plan created");
      onCreated?.(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not create plan"));
    } finally { setSaving(false); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[92vh] overflow-y-auto bg-white sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display">New Production Plan</DialogTitle>
            <DialogDescription className="text-xs">
              Pick products and planned quantities. The system loads each product's <strong>approved recipe</strong>,
              calculates ingredient requirements and shows raw-material availability. No stock is reserved here.
            </DialogDescription>
          </DialogHeader>

          {prefill?.length > 0 && (
            <div data-testid="plan-prefill-banner" className="flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <b>Prefilled from Order Consolidation.</b> {prefillNote}
                {prefillSourceOrders?.length > 0 && <div className="mt-0.5">Source orders: <span className="font-mono">{prefillSourceOrders.join(", ")}</span></div>}
                <div className="mt-0.5">Review quantities and confirm — creating this plan does not reserve stock or issue raw materials.</div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 py-1 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>Section</Label>
              <div className="flex gap-1.5">
                {PRODUCT_TYPES.map((t) => (
                  <Button key={t.value} type="button" size="sm" variant={section === t.value ? "default" : "outline"}
                    data-testid={`plan-section-${t.value}`}
                    className={section === t.value ? "h-9 flex-1 bg-amber-700 hover:bg-amber-800" : "h-9 flex-1"}
                    onClick={() => { setSection(t.value); setLines([{ productId: "", plannedQty: "", recipe: null, loading: false }]); }}>
                    {t.label}
                  </Button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Plan Date</Label>
              <Input data-testid="plan-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 text-sm" />
            </div>
          </div>

          <div className="mt-1 rounded-lg border border-stone-200">
            <div className="flex items-center justify-between border-b border-stone-200 bg-stone-50 px-4 py-2">
              <span className="text-sm font-semibold text-stone-800">Products to produce</span>
              <Button data-testid="plan-add-line" size="sm" variant="outline" className="h-8 gap-1.5" onClick={addLine}>
                <Plus className="h-3.5 w-3.5" /> Add product
              </Button>
            </div>
            <div className="divide-y divide-stone-100">
              {built.map((b, i) => {
                const p = b.p;
                const approved = p && hasApprovedRecipe(p);
                return (
                  <div key={i} className="space-y-2 px-3 py-3">
                    <div className="grid grid-cols-12 items-center gap-2">
                      <div className="col-span-7">
                        <Combobox testId={`plan-line-prod-${i}`} value={b.l.productId}
                          onChange={(v) => pickProduct(i, v)} placeholder="Search product…"
                          options={sectionProducts.map((sp) => ({ value: sp.id, label: sp.name, hint: sp.productId }))} />
                      </div>
                      <Input data-testid={`plan-line-qty-${i}`} className="col-span-4 h-9 text-sm" type="number" min="0"
                        placeholder="Planned qty" value={b.l.plannedQty} onChange={(e) => setLine(i, { plannedQty: e.target.value })} />
                      <button data-testid={`plan-line-remove-${i}`} className="col-span-1 text-stone-400 hover:text-rose-600" onClick={() => removeLine(i)}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    {p && b.l.loading && <div className="flex items-center gap-1.5 text-xs text-stone-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading approved recipe…</div>}

                    {p && !approved && (
                      <div data-testid={`plan-line-noapprove-${i}`} className="flex items-center gap-1.5 rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-xs text-rose-700">
                        <XCircle className="h-3.5 w-3.5" /> No <strong>approved</strong> recipe for this product — production is blocked. Approve a recipe first.
                      </div>
                    )}

                    {approved && b.l.recipe && Number(b.l.plannedQty) > 0 && (
                      <div className="rounded-md border border-stone-200 bg-stone-50/60 px-3 py-2">
                        <div className="mb-1 flex items-center gap-2 text-[11px] text-stone-500">
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                          Recipe v{p.currentRecipeVersion} · yield {fmtNumber(b.l.recipe.yieldQty)} {b.l.recipe.yieldUnit} · requirements for {fmtNumber(b.l.plannedQty)} {p.productUnit || "pcs"}
                        </div>
                        <div className="space-y-1">
                          {b.requirements.map((r) => {
                            const av = availMap[r.ingredientId];
                            const enough = av && av.available >= r.requiredQty;
                            return (
                              <div key={r.ingredientId} data-testid={`plan-req-${i}-${r.ingredientId}`} className="flex items-center justify-between text-xs">
                                <span className="text-stone-700">{r.name}</span>
                                <span className="flex items-center gap-2 font-mono">
                                  <span>{fmtNumber(r.requiredQty, 3)} {r.unit}</span>
                                  <span className={enough ? "text-emerald-600" : "text-rose-600"}>
                                    {av ? `avail ${fmtNumber(av.available, 3)}` : "not in stock"}
                                  </span>
                                </span>
                              </div>
                            );
                          })}
                        </div>
                        {Array.isArray(b.l.recipe.packaging) && b.l.recipe.packaging.length > 0 && (
                          <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-stone-500">
                            <Package className="h-3.5 w-3.5" /> Packaging: {b.l.recipe.packaging.map((pk) => pk.name || pk.material).filter(Boolean).join(", ") || `${b.l.recipe.packaging.length} item(s)`}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {aggregated.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-900">
              <div className="mb-1 font-semibold">Total ingredient requirement (all lines)</div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 sm:grid-cols-3">
                {aggregated.map((a) => (
                  <div key={a.ingredientId} className="flex justify-between font-mono">
                    <span className="truncate pr-2">{a.name}</span><span>{fmtNumber(a.qty, 3)} {a.unit}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {blockedLines.length > 0 && (
            <div className="flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              <AlertTriangle className="h-4 w-4" /> Remove or replace lines without an approved recipe before saving.
            </div>
          )}

          <DialogFooter className="mt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="plan-cancel">Cancel</Button>
            <Button className="bg-amber-700 hover:bg-amber-800" disabled={!canSave || saving} data-testid="plan-save" onClick={() => setConfirmOpen(true)}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-amber-600" /> Create production plan</AlertDialogTitle>
            <AlertDialogDescription>
              This creates a <strong>{productTypeLabel(section)}</strong> production plan with {readyLines.length} product line(s) in the shared ERP.
              No ingredients are reserved and no stock moves — reservation is a separate, explicit step. Proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="plan-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="plan-confirm-save" className="bg-amber-700 hover:bg-amber-800" onClick={doCreate}>Yes, create plan</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
